"use client";

import { useMemo } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  CreditCard,
  MessageSquare,
  Phone,
  Plug,
  Settings,
  Sparkles,
  Users,
  type LucideIcon,
} from "lucide-react";
import { appDashboardUrl } from "~/lib/auth-paths";
import {
  getHelpArticle,
  getHelpTopic,
  HELP_TOPICS,
  type HelpTopic,
} from "~/lib/inbound-gpt/help-center-content";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";
import { adaptMarketingCopyForBrand } from "~/lib/siteglue/adapt-marketing-copy";

function HelpRelatedLink({ href, label }: { href: string; label: string }) {
  const isHireCta = href.startsWith("/agents/hire/custom");
  if (isHireCta) {
    return (
      <a
        href={appDashboardUrl(href)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
      >
        {label}
        <ArrowRight className="h-3.5 w-3.5" />
      </a>
    );
  }
  return (
    <Link
      href={href}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
    >
      {label}
      <ArrowRight className="h-3.5 w-3.5" />
    </Link>
  );
}

const TOPIC_ICONS: Record<HelpTopic["icon"], LucideIcon> = {
  settings: Settings,
  chat: MessageSquare,
  phone: Phone,
  training: BookOpen,
  billing: CreditCard,
  integrations: Plug,
  handoff: Users,
  agents: Sparkles,
};

function HelpFooterLinks() {
  return (
    <p className="text-muted-foreground">
      Can&apos;t find what you&apos;re looking for? Check our{" "}
      <Link href="/faq" className="text-primary hover:underline" data-testid="link-help-faq">
        FAQ
      </Link>
      , browse the{" "}
      <Link
        href="/documentation"
        className="text-primary hover:underline"
        data-testid="link-help-documentation"
      >
        Documentation
      </Link>
      , or{" "}
      <Link href="/about" className="text-primary hover:underline" data-testid="link-help-contact">
        contact us
      </Link>{" "}
      for personalized support.
    </p>
  );
}

export function HelpCenterPageContent() {
  const brand = getMarketingChrome().brandId;
  const a = (s: string) => adaptMarketingCopyForBrand(s, brand);
  const topics = useMemo(
    () =>
      HELP_TOPICS.map((topic) => ({
        ...topic,
        title: a(topic.title),
        description: a(topic.description),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brand],
  );

  return (
    <div className="pb-16 pt-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-bold tracking-tight sm:text-5xl"
          >
            Help <span className="text-primary">Center</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground"
          >
            {a(
              "Guides for setup, channels, unit-based pricing, integrations, and keeping a human in the loop — so you never miss a conversation.",
            )}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {topics.map((topic, index) => {
            const Icon = TOPIC_ICONS[topic.icon];
            return (
              <motion.div
                key={topic.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.04 }}
              >
                <Link
                  href={`/help-center/${topic.id}`}
                  className="flex h-full flex-col rounded-xl border border-border bg-card p-6 transition-colors hover:border-primary/40 hover:bg-primary/5"
                  data-testid={`card-help-${topic.id}`}
                >
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <h2 className="text-lg font-bold tracking-tight">{topic.title}</h2>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {topic.description}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                    {topic.articles.length} articles
                    <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-16 text-center">
          <HelpFooterLinks />
        </div>
      </div>
    </div>
  );
}

export function HelpTopicPageContent({ topicId }: { topicId: string }) {
  const brand = getMarketingChrome().brandId;
  const a = (s: string) => adaptMarketingCopyForBrand(s, brand);
  const topic = getHelpTopic(topicId);
  if (!topic) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6">
        <h1 className="text-2xl font-bold">Topic not found</h1>
        <Link href="/help-center" className="mt-4 inline-flex text-primary hover:underline">
          Back to Help Center
        </Link>
      </div>
    );
  }

  const Icon = TOPIC_ICONS[topic.icon];

  return (
    <div className="pb-16 pt-8">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <Link
          href="/help-center"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Help Center
        </Link>

        <div className="mt-6 flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <Icon className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{a(topic.title)}</h1>
            <p className="mt-2 text-lg text-muted-foreground">{a(topic.description)}</p>
          </div>
        </div>

        <ul className="mt-10 space-y-3">
          {topic.articles.map((article) => (
            <li key={article.id}>
              <Link
                href={`/help-center/${topic.id}/${article.id}`}
                className="block rounded-xl border border-border bg-card px-5 py-4 transition-colors hover:border-primary/40 hover:bg-primary/5"
                data-testid={`link-help-article-${article.id}`}
              >
                <span className="text-base font-semibold text-foreground">{a(article.title)}</span>
                <p className="mt-1 text-sm text-muted-foreground">{a(article.summary)}</p>
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-12 border-t border-border pt-8 text-center">
          <HelpFooterLinks />
        </div>
      </div>
    </div>
  );
}

export function HelpArticlePageContent({
  topicId,
  articleId,
}: {
  topicId: string;
  articleId: string;
}) {
  const brand = getMarketingChrome().brandId;
  const a = (s: string) => adaptMarketingCopyForBrand(s, brand);
  const resolved = getHelpArticle(topicId, articleId);
  if (!resolved) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6">
        <h1 className="text-2xl font-bold">Article not found</h1>
        <Link href="/help-center" className="mt-4 inline-flex text-primary hover:underline">
          Back to Help Center
        </Link>
      </div>
    );
  }

  const { topic, article } = resolved;

  return (
    <div className="pb-16 pt-8">
      <article className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <nav className="mb-8 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <Link href="/help-center" className="hover:text-foreground">
            Help Center
          </Link>
          <span>/</span>
          <Link href={`/help-center/${topic.id}`} className="hover:text-foreground">
            {a(topic.title)}
          </Link>
          <span>/</span>
          <span className="text-foreground">{a(article.title)}</span>
        </nav>

        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{a(article.title)}</h1>
        <p className="mt-3 text-lg text-muted-foreground">{a(article.summary)}</p>

        <div className="mt-8 space-y-4">
          {article.body.map((paragraph, index) => (
            <p key={index} className="text-base leading-relaxed text-foreground/90">
              {a(paragraph)}
            </p>
          ))}
        </div>

        {article.relatedLinks?.length ? (
          <div className="mt-10 rounded-xl border border-border bg-muted/30 p-5">
            <p className="text-sm font-semibold text-foreground">Related</p>
            <ul className="mt-3 space-y-2">
              {article.relatedLinks.map((link) => (
                <li key={link.href + link.label}>
                  <HelpRelatedLink href={link.href} label={link.label} />
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="mt-10 flex flex-wrap gap-4 border-t border-border pt-8">
          <Link
            href={`/help-center/${topic.id}`}
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            More in {a(topic.title)}
          </Link>
          <Link href="/documentation" className="text-sm text-primary hover:underline">
            Full documentation
          </Link>
          <Link href="/pricing" className="text-sm text-primary hover:underline">
            Pricing calculator
          </Link>
        </div>
      </article>
    </div>
  );
}
